/**
 * Mention Picker Component
 * 
 * Dropdown picker for selecting participants to mention in group chats.
 * Triggered by typing @ in message input.
 */ 

import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import { List, Avatar, Typography } from 'antd';
import styled from '@emotion/styled';

const { Text } = Typography;

interface Participant {
  userId: string;
  name: string;
  avatar?: string;
}

interface MentionPickerProps {
  participants: Participant[];
  searchQuery: string;
  onSelect: (participant: Participant) => void;
  open: boolean;
  onClose: () => void;
  placement?: 'top' | 'bottom';
}

const PickerContainer = styled.div<{ placement: 'top' | 'bottom' }>`
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 200px;
  overflow-y: auto;
  min-width: 200px;
  max-width: 300px;
  z-index: 1000;
`;

const ParticipantItem = styled(List.Item)`
  cursor: pointer;
  padding: 8px 12px !important;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f5f5f5;
  }
  
  &.selected {
    background-color: #e6f7ff;
  }
`;

const ParticipantName = styled(Text)`
  font-size: 14px;
  color: #262626;
`;

/**
 * Get initials from a name
 */
const getInitials = (name: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Generate a consistent color from a string
 */
const getAvatarColor = (name: string): string => {
  const colors = [
    '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1',
    '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb'
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export const MentionPicker: React.FC<MentionPickerProps> = ({
  participants,
  searchQuery,
  onSelect,
  open,
  onClose,
  placement = 'top',
}) => {
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);

  // Filter participants based on search query
  const filteredParticipants = useMemo(() => {
    if (!searchQuery) {
      return participants;
    }
    
    const query = searchQuery.toLowerCase().trim();
    return participants.filter((participant) =>
      participant.name.toLowerCase().includes(query)
    );
  }, [participants, searchQuery]);

  // Reset selected index when filtered results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredParticipants.length, searchQuery]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedItemRef.current && listRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredParticipants.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredParticipants[selectedIndex]) {
          onSelect(filteredParticipants[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, filteredParticipants, selectedIndex, onSelect, onClose]);

  const handleParticipantClick = useCallback(
    (participant: Participant) => {
      onSelect(participant);
    },
    [onSelect]
  );

  if (!open || filteredParticipants.length === 0) {
    return null;
  }

  return (
    <PickerContainer placement={placement} ref={listRef}>
      <List
        dataSource={filteredParticipants}
        renderItem={(participant, index) => (
          <ParticipantItem
            ref={index === selectedIndex ? selectedItemRef : undefined}
            className={index === selectedIndex ? 'selected' : ''}
            onClick={() => handleParticipantClick(participant)}
          >
            <List.Item.Meta
              avatar={
                <Avatar
                  src={participant.avatar}
                  style={{
                    backgroundColor: participant.avatar
                      ? undefined
                      : getAvatarColor(participant.name),
                  }}
                >
                  {!participant.avatar && getInitials(participant.name)}
                </Avatar>
              }
              title={<ParticipantName>{participant.name}</ParticipantName>}
            />
          </ParticipantItem>
        )}
      />
    </PickerContainer>
  );
};

